import React, { useEffect, useRef, useState } from 'react';

interface DocxViewerWithHighlightProps {
  docxHtml: string;
  height?: string;
  /** Sentence text to scroll to (separate from highlight keyword) */
  scrollTarget?: string;
  /** Whether a keyword highlight exists (to scroll to #docx-highlight) */
  hasHighlight?: boolean;
  /** Zero-based keyword match to scroll to when every match is highlighted */
  highlightMatchIndex?: number;
  /** Whether the content is an EML file — limits highlight to a single block to avoid breaking rich HTML layout */
  isEml?: boolean;
}

// ===========================================================================
// DOM-AWARE TEXT HIGHLIGHTING
// ===========================================================================

// Block-level tag names that create paragraph/content boundaries.
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'TR',
  'TD',
  'TH',
  'BLOCKQUOTE',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'FOOTER',
  'BR',
  'HR',
]);

interface TextEntry {
  node: Text;
  flatStart: number;
  flatEnd: number;
}

/** Walk up from a node to find its nearest block-level ancestor within container. */
function getBlockAncestor(container: HTMLElement, node: Node): Element | null {
  let el = node.parentElement;
  while (el && el !== container) {
    if (BLOCK_TAGS.has(el.tagName)) return el;
    el = el.parentElement;
  }
  return null;
}

/** Check whether a node is inside an element with class "email-header". */
function isInsideEmailHeader(node: Node): boolean {
  let el = node.parentElement;
  while (el) {
    if (el.classList.contains('email-header')) return true;
    el = el.parentElement;
  }
  return false;
}

/**
 * Word footnote marks ("[1]") sit inside the sentence on screen but not in the
 * document text a citation is quoted from, so they are left out of the search.
 */
function isFootnoteMark(node: Node): boolean {
  return Boolean(
    node.parentElement?.closest(
      '.docx-footnote-ref, a[id^="footnote-ref-"], a[id^="endnote-ref-"]',
    ),
  );
}

/** Walk text nodes under container, building a flat text string and per-node index. */
function collectTextEntries(container: HTMLElement): { entries: TextEntry[]; flatText: string } {
  const entries: TextEntry[] = [];
  let flatText = '';
  let lastBlockAncestor: Element | null = null;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let textNode: Text | null;
  while ((textNode = walker.nextNode() as Text | null)) {
    const text = textNode.textContent || '';
    if (!text) continue;
    if (isInsideEmailHeader(textNode)) continue;
    if (isFootnoteMark(textNode)) continue;

    const blockAncestor = getBlockAncestor(container, textNode);
    if (
      entries.length > 0 &&
      blockAncestor !== lastBlockAncestor &&
      flatText.length > 0 &&
      !/\s$/.test(flatText)
    ) {
      flatText += ' ';
    }

    const start = flatText.length;
    flatText += text;
    entries.push({ node: textNode, flatStart: start, flatEnd: flatText.length });
    lastBlockAncestor = blockAncestor;
  }

  return { entries, flatText };
}

/** Markdown formatting chars stripped during normalization so that plain-text
 *  needles can still match sources that contain markdown syntax. */
const MARKDOWN_STRIP_CHARS = new Set(['*', '_', '`', '>', '#', '\\', '-']);

const QUOTE_FOLDS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '“': '"',
  '”': '"',
};

const KEPT_CHAR = /[\p{L}\p{N}'"]/u;

/**
 * Mirrors the chatbot's reference-match normalisation: citation snippets come
 * back with table cells joined by " | " or with punctuation replaced by
 * spaces, so any other punctuation has to count as a word gap on both sides.
 * Returns '' to drop the char, ' ' for a word gap, or the char to keep.
 */
function normalizeSearchChar(ch: string): string {
  if (MARKDOWN_STRIP_CHARS.has(ch)) return '';
  const folded = QUOTE_FOLDS[ch] ?? ch;
  return KEPT_CHAR.test(folded) ? folded : ' ';
}

/** Strip markdown formatting chars and collapse whitespace (for search needles). */
function stripForSearch(s: string): string {
  let out = '';
  for (const ch of s) {
    out += normalizeSearchChar(ch);
  }
  return out.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Collapse whitespace runs + strip markdown chars + lowercase; keep a map back to original indices. */
function buildNormalizedMapping(source: string): { normalized: string; toOrig: number[] } {
  const toOrig: number[] = [];
  let normalized = '';
  let inWs = false;
  let started = false;

  for (let i = 0; i < source.length; i++) {
    const ch = normalizeSearchChar(source[i]);
    if (!ch) continue;
    if (ch === ' ') {
      if (started && !inWs) {
        normalized += ' ';
        toOrig.push(i);
        inWs = true;
      }
    } else {
      started = true;
      normalized += ch;
      toOrig.push(i);
      inWs = false;
    }
  }
  if (normalized.endsWith(' ')) {
    normalized = normalized.slice(0, -1);
    toOrig.pop();
  }
  return { normalized: normalized.toLowerCase(), toOrig };
}

/** Create a highlight <mark> element with the standard style. */
function makeMark(text: string, id?: string): HTMLElement {
  const mark = document.createElement('mark');
  mark.style.cssText = 'background:#ffe58f;padding:2px 0;';
  if (id) mark.id = id;
  mark.textContent = text;
  return mark;
}

/**
 * Internal: attempt to highlight a search string in the HTML.
 *
 * When the search string appears multiple times in the document, the
 * optional context (contextBefore / contextAfter) is used to score each
 * occurrence and pick the best match.
 *
 * Returns the modified HTML if a match is found, or null if not.
 */
function _tryHighlight(
  rawHtml: string,
  searchText: string,
  context?: { contextBefore?: string; contextAfter?: string },
): string | null {
  if (!searchText) return null;

  const container = document.createElement('div');
  container.innerHTML = rawHtml;

  const { entries, flatText } = collectTextEntries(container);
  if (entries.length === 0) return null;

  const { normalized: normFlat, toOrig: normToFlat } = buildNormalizedMapping(flatText);
  const normSearch = stripForSearch(searchText);

  if (!normSearch) return null;

  // Find ALL occurrences of the search text
  const allMatches: number[] = [];
  let searchFrom = 0;
  while (true) {
    const idx = normFlat.indexOf(normSearch, searchFrom);
    if (idx === -1) break;
    allMatches.push(idx);
    searchFrom = idx + 1;
  }

  if (allMatches.length === 0) return null;

  // Pick the best match using context disambiguation
  let bestMatchIdx = allMatches[0];

  if (allMatches.length > 1 && context) {
    const normCtxBefore = stripForSearch(context.contextBefore || '');
    const normCtxAfter = stripForSearch(context.contextAfter || '');

    let bestScore = -1;
    for (const candidateIdx of allMatches) {
      let score = 0;
      const candidateEnd = candidateIdx + normSearch.length;

      if (normCtxBefore) {
        const lookBackLen = Math.min(candidateIdx, normCtxBefore.length + 40);
        const textBefore = normFlat.substring(candidateIdx - lookBackLen, candidateIdx);
        if (textBefore.includes(normCtxBefore)) {
          score += 4;
        } else if (normCtxBefore.length >= 20 && textBefore.includes(normCtxBefore.slice(-20))) {
          score += 2;
        } else if (normCtxBefore.length >= 10 && textBefore.includes(normCtxBefore.slice(-10))) {
          score += 1;
        }
      }

      if (normCtxAfter) {
        const lookAheadLen = Math.min(normFlat.length - candidateEnd, normCtxAfter.length + 40);
        const textAfter = normFlat.substring(candidateEnd, candidateEnd + lookAheadLen);
        if (textAfter.includes(normCtxAfter)) {
          score += 4;
        } else if (normCtxAfter.length >= 20 && textAfter.includes(normCtxAfter.slice(0, 20))) {
          score += 2;
        } else if (normCtxAfter.length >= 10 && textAfter.includes(normCtxAfter.slice(0, 10))) {
          score += 1;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatchIdx = candidateIdx;
      }
    }
  }

  // Map back to flat text positions
  const matchEndNorm = bestMatchIdx + normSearch.length;
  const flatStart = normToFlat[bestMatchIdx];
  const flatEnd = matchEndNorm >= normToFlat.length ? flatText.length : normToFlat[matchEndNorm];

  // Identify affected text nodes
  interface AffectedNode {
    node: Text;
    hlStart: number;
    hlEnd: number;
  }
  const affected: AffectedNode[] = [];
  for (const entry of entries) {
    if (entry.flatEnd <= flatStart || entry.flatStart >= flatEnd) continue;
    affected.push({
      node: entry.node,
      hlStart: Math.max(0, flatStart - entry.flatStart),
      hlEnd: Math.min(entry.flatEnd - entry.flatStart, flatEnd - entry.flatStart),
    });
  }
  if (affected.length === 0) return null;

  // Wrap matched text with <mark> elements
  for (let i = affected.length - 1; i >= 0; i--) {
    const { node, hlStart, hlEnd } = affected[i];
    const text = node.textContent || '';
    const parent = node.parentNode;
    if (!parent) continue;

    const fragment = document.createDocumentFragment();
    const before = text.substring(0, hlStart);
    const matched = text.substring(hlStart, hlEnd);
    const after = text.substring(hlEnd);

    if (before) fragment.appendChild(document.createTextNode(before));
    fragment.appendChild(makeMark(matched, i === 0 ? 'docx-highlight' : undefined));
    if (after) fragment.appendChild(document.createTextNode(after));

    parent.replaceChild(fragment, node);
  }

  return container.innerHTML;
}

/** Group affected text nodes by node, merge overlapping ranges, and apply marks in reverse DOM order. */
function applyRangesToNodes(
  allAffected: { node: Text; hlStart: number; hlEnd: number }[],
  makeMarkNode: (text: string) => Node,
): void {
  const nodeRanges = new Map<Text, { hlStart: number; hlEnd: number }[]>();
  for (const a of allAffected) {
    if (!nodeRanges.has(a.node)) nodeRanges.set(a.node, []);
    nodeRanges.get(a.node)!.push({ hlStart: a.hlStart, hlEnd: a.hlEnd });
  }
  const nodeOrder = [...nodeRanges.keys()];
  for (let ni = nodeOrder.length - 1; ni >= 0; ni--) {
    const node = nodeOrder[ni];
    const ranges = nodeRanges.get(node)!;
    ranges.sort((a, b) => a.hlStart - b.hlStart);
    const merged: { hlStart: number; hlEnd: number }[] = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      if (last && r.hlStart <= last.hlEnd) last.hlEnd = Math.max(last.hlEnd, r.hlEnd);
      else merged.push({ ...r });
    }
    const text = node.textContent ?? '';
    const parent = node.parentNode;
    if (!parent) continue;
    const fragment = document.createDocumentFragment();
    let lastEnd = 0;
    for (const { hlStart, hlEnd } of merged) {
      if (hlStart > lastEnd)
        fragment.appendChild(document.createTextNode(text.substring(lastEnd, hlStart)));
      fragment.appendChild(makeMarkNode(text.substring(hlStart, hlEnd)));
      lastEnd = hlEnd;
    }
    if (lastEnd < text.length)
      fragment.appendChild(document.createTextNode(text.substring(lastEnd)));
    parent.replaceChild(fragment, node);
  }
}

/**
 * Internal: attempt to highlight every occurrence of searchText in rawHtml.
 *
 * Returns the modified HTML if at least one match is found, or null if not.
 */
function _tryHighlightAll(rawHtml: string, searchText: string): string | null {
  if (!searchText) return null;

  const container = document.createElement('div');
  container.innerHTML = rawHtml;

  const { entries, flatText } = collectTextEntries(container);
  if (entries.length === 0) return null;

  const { normalized, toOrig } = buildNormalizedMapping(flatText);

  const normSearch = stripForSearch(searchText);
  if (!normSearch) return null;

  const allMatches: number[] = [];
  let searchFrom = 0;
  while (true) {
    const idx = normalized.indexOf(normSearch, searchFrom);
    if (idx === -1) break;
    allMatches.push(idx);
    searchFrom = idx + 1;
  }
  if (allMatches.length === 0) return null;

  interface AffectedNode {
    node: Text;
    hlStart: number;
    hlEnd: number;
  }
  const allAffected: AffectedNode[] = [];

  for (const matchIdx of allMatches) {
    const matchEndNorm = matchIdx + normSearch.length;
    const flatStart = toOrig[matchIdx];
    const flatEnd = matchEndNorm >= toOrig.length ? flatText.length : toOrig[matchEndNorm];

    for (const entry of entries) {
      if (entry.flatEnd <= flatStart || entry.flatStart >= flatEnd) continue;
      allAffected.push({
        node: entry.node,
        hlStart: Math.max(0, flatStart - entry.flatStart),
        hlEnd: Math.min(entry.flatEnd - entry.flatStart, flatEnd - entry.flatStart),
      });
    }
  }
  if (allAffected.length === 0) return null;

  applyRangesToNodes(allAffected, (text) => makeMark(text));

  return container.innerHTML;
}

export function highlightTextInHtml(
  rawHtml: string,
  searchText: string,
  context?: { contextBefore?: string; contextAfter?: string },
): string {
  if (!rawHtml) return rawHtml;
  if (!searchText) return rawHtml;

  const result = _tryHighlight(rawHtml, searchText, context);
  if (result !== null) return result;

  return rawHtml;
}

export function highlightAllInHtml(rawHtml: string, searchText: string): string {
  if (!rawHtml) return rawHtml;
  if (!searchText) return rawHtml;

  const result = _tryHighlightAll(rawHtml, searchText);
  if (result !== null) return result;

  return rawHtml;
}

/**
 * Highlight all occurrences of searchText in salmon pink with class
 * `docx-scroll-highlight` so the component can find them for navigation.
 */
export function highlightAllScrollTargetsInHtml( // NOSONAR
  rawHtml: string,
  searchText: string,
  color = 'rgba(250, 163, 138, 0.55)',
): string {
  if (!rawHtml || !searchText) return rawHtml;

  const container = document.createElement('div');
  container.innerHTML = rawHtml;

  const { entries, flatText } = collectTextEntries(container);
  if (!entries.length) return rawHtml;

  const { normalized, toOrig } = buildNormalizedMapping(flatText);
  const normSearch = stripForSearch(searchText);
  if (!normSearch) return rawHtml;

  const allMatches: number[] = [];
  let from = 0;
  while (true) {
    const idx = normalized.indexOf(normSearch, from);
    if (idx === -1) break;
    allMatches.push(idx);
    from = idx + 1;
  }
  if (!allMatches.length) return rawHtml;

  interface MatchRange {
    flatStart: number;
    flatEnd: number;
  }
  const matchRanges: MatchRange[] = allMatches.map((normIdx) => {
    const normEnd = normIdx + normSearch.length;
    return {
      flatStart: toOrig[normIdx],
      flatEnd: normEnd >= toOrig.length ? flatText.length : toOrig[normEnd],
    };
  });

  interface AffectedNode {
    node: Text;
    hlStart: number;
    hlEnd: number;
  }
  const allAffected: AffectedNode[] = [];
  for (const { flatStart, flatEnd } of matchRanges) {
    for (const entry of entries) {
      if (entry.flatEnd <= flatStart || entry.flatStart >= flatEnd) continue;
      allAffected.push({
        node: entry.node,
        hlStart: Math.max(0, flatStart - entry.flatStart),
        hlEnd: Math.min(entry.flatEnd - entry.flatStart, flatEnd - entry.flatStart),
      });
    }
  }
  if (!allAffected.length) return rawHtml;

  applyRangesToNodes(allAffected, (text) => {
    const mark = document.createElement('mark');
    mark.className = 'docx-scroll-highlight';
    mark.style.cssText = `background-color: ${color}; padding: 2px 0;`;
    mark.textContent = text;
    return mark;
  });

  return container.innerHTML;
}

/**
 * Returns number of exact matches found by highlightAllScrollTargetsInHtml.
 * Used by callers to determine exact vs fuzzy match type.
 */
export function countScrollTargetMatches(rawHtml: string, searchText: string): number {
  if (!rawHtml || !searchText) return 0;
  const container = document.createElement('div');
  container.innerHTML = rawHtml;
  const { entries, flatText } = collectTextEntries(container);
  if (!entries.length) return 0;
  const { normalized } = buildNormalizedMapping(flatText);
  const normSearch = stripForSearch(searchText);
  if (!normSearch) return 0;
  let count = 0;
  let from = 0;
  while (true) {
    const idx = normalized.indexOf(normSearch, from);
    if (idx === -1) break;
    count++;
    from = idx + 1;
  }
  return count;
}

/**
 * Finds the paragraph/block in the DOCX HTML most similar to searchText
 * and wraps it with a light-green fuzzy highlight mark.
 * Only called when no exact match exists.
 *
 * Scoring rationale:
 * - Only "significant" words (longer than 4 chars) are compared, so common
 *   filler words ("the", "and", "with", ...) cannot inflate similarity.
 * - Each block is scored by query coverage: the fraction of the query's
 *   significant words found in the block (overlap / searchWords.length).
 * - A block only qualifies when it covers at least 30% of the query AND
 *   shares a minimum absolute number of words (see minWordOverlap below).
 *   The ratio alone is unreliable for short queries — with 3 significant
 *   words a single shared word already scores 0.33 — so the absolute floor
 *   guarantees one coincidental word in common never triggers a highlight.
 */
export function addFuzzyHighlightToHtml(
  rawHtml: string,
  searchText: string,
  color = 'rgba(134, 239, 172, 0.55)',
): string {
  if (!rawHtml || !searchText) return rawHtml;
  const normSearch = stripForSearch(searchText);
  if (!normSearch) return rawHtml;
  const searchWords = normSearch.split(' ').filter((w) => w.length > 4);
  if (searchWords.length === 0) return rawHtml;

  // Minimum absolute overlap: single-word queries must match their one word;
  // every longer query must share at least 2 significant words with a block.
  const minWordOverlap = Math.min(2, searchWords.length);

  const container = document.createElement('div');
  container.innerHTML = rawHtml;

  const blocks = Array.from(
    container.querySelectorAll<HTMLElement>('p, li, td, h1, h2, h3, h4, h5, h6'),
  );

  let bestScore = 0;
  let bestBlock: HTMLElement | null = null;
  for (const block of blocks) {
    if (block.querySelector('p, li')) continue; // skip wrappers
    const blockNorm = stripForSearch(block.textContent ?? '');
    const blockWords = new Set(blockNorm.split(' ').filter((w) => w.length > 4));
    const overlap = searchWords.filter((w) => blockWords.has(w)).length;
    const score = overlap / searchWords.length;
    if (overlap >= minWordOverlap && score > bestScore) {
      bestScore = score;
      bestBlock = block;
    }
  }

  // 0.3 keeps recall high for reworded passages (the fuzzy fallback only runs
  // when no exact match exists) while the minWordOverlap floor above filters
  // out the short-query false positives a bare ratio would let through.
  if (bestBlock && bestScore >= 0.3) {
    const mark = document.createElement('mark');
    mark.className = 'docx-fuzzy-highlight';
    mark.style.cssText = `background-color: ${color}; padding: 2px 0;`;
    while (bestBlock.firstChild) mark.appendChild(bestBlock.firstChild);
    bestBlock.appendChild(mark);
  }
  return container.innerHTML;
}

/** Split an HTML string into chunks at block-level boundaries. */
function splitHtmlIntoChunks(html: string, blocksPerChunk = 40): string[] {
  const regex = /<\/(?:p|div|table|ul|ol|li|h[1-6]|blockquote|tr|thead|tbody)>/gi;
  const chunks: string[] = [];
  let lastIndex = 0;
  let count = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(html)) !== null) {
    count++;
    if (count % blocksPerChunk === 0) {
      const end = match.index + match[0].length;
      chunks.push(html.slice(lastIndex, end));
      lastIndex = end;
    }
  }
  if (lastIndex < html.length) chunks.push(html.slice(lastIndex));
  return chunks.length > 0 ? chunks : [html];
}

const DocxViewerWithHighlight: React.FC<DocxViewerWithHighlightProps> = ({
  docxHtml,
  height = '100%',
  hasHighlight,
  highlightMatchIndex,
  isEml,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollMarksRef = useRef<HTMLElement[]>([]);
  const [scrollMatchCount, setScrollMatchCount] = useState(0);
  const [scrollMatchIndex, setScrollMatchIndex] = useState(0);

  const goToMark = (index: number) => {
    const clamped = Math.max(0, Math.min(index, scrollMarksRef.current.length - 1));
    setScrollMatchIndex(clamped);
    scrollMarksRef.current[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = '';
    setScrollMatchCount(0);
    setScrollMatchIndex(0);
    scrollMarksRef.current = [];
    if (!docxHtml) return;

    const chunks = splitHtmlIntoChunks(docxHtml);
    let chunkIndex = 0;
    let rafId: number;

    const insertNext = () => {
      if (chunkIndex >= chunks.length) {
        // All chunks inserted — find salmon pink scroll marks for navigation
        const marks = Array.from(
          container.querySelectorAll<HTMLElement>('mark.docx-scroll-highlight'),
        );
        scrollMarksRef.current = marks;
        setScrollMatchCount(marks.length);
        setScrollMatchIndex(0);

        if (marks.length > 0) {
          marks[0].scrollIntoView({ behavior: 'auto', block: 'center' });
        } else if (hasHighlight) {
          if (highlightMatchIndex !== undefined) {
            const kwMarks = Array.from(
              container.querySelectorAll('mark:not(.docx-scroll-highlight)'),
            );
            if (kwMarks.length > 0) {
              const idx =
                ((highlightMatchIndex % kwMarks.length) + kwMarks.length) % kwMarks.length;
              kwMarks[idx].scrollIntoView({ behavior: 'auto', block: 'center' });
              return;
            }
          }
          const el = container.querySelector('#docx-highlight');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      container.insertAdjacentHTML('beforeend', chunks[chunkIndex]);
      chunkIndex++;
      rafId = requestAnimationFrame(insertNext);
    };

    rafId = requestAnimationFrame(insertNext);
    return () => cancelAnimationFrame(rafId);
  }, [docxHtml, hasHighlight, highlightMatchIndex]);

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      {scrollMatchCount > 1 && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            borderRadius: 6,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            userSelect: 'none',
          }}
        >
          <button
            type="button"
            style={{
              cursor: 'pointer',
              padding: '0 4px',
              background: 'none',
              border: 'none',
              fontSize: 'inherit',
            }}
            onClick={() => goToMark(scrollMatchIndex - 1)}
          >
            ▲
          </button>
          <span>
            {scrollMatchIndex + 1} / {scrollMatchCount}
          </span>
          <button
            type="button"
            style={{
              cursor: 'pointer',
              padding: '0 4px',
              background: 'none',
              border: 'none',
              fontSize: 'inherit',
            }}
            onClick={() => goToMark(scrollMatchIndex + 1)}
          >
            ▼
          </button>
        </div>
      )}
      <div
        ref={containerRef}
        className={isEml ? 'eml-body-content' : undefined}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          backgroundColor: '#fff',
          padding: '16px',
        }}
      />
    </div>
  );
};

export default DocxViewerWithHighlight;
